// client/src/components/pages/LexiStar/Reports/ReportBody.tsx
//
// The body of a standard report (ST-00188 M4-R2; FR-RPT-002): the provider's
// headline figures as stat tiles, a chart of the primary table when it has a
// date or category axis (Recharts; one axis, at most three same-unit series in
// the validated categorical order, legend from two series, the table below is
// the chart's table view), then every table with its totals, then the notes.
// The same body prints — M4-R1's XLSX/CSV carry the same tables.

import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { fmtDateTime, fmtDuration } from '../StatusBoard/format';
import { Prov, Section } from './reportAtoms';
import type { HeadlineFigure, ProviderReport, ProviderTable, ReportColumn } from '../../../../types/lexistar-reports';

/** Categorical slots 1–3 (dataviz reference palette, validated on white paper: CVD ΔE ≥ 9.2). */
export const SERIES_COLORS = ['#2a78d6', '#eb6834', '#1baf7a'];
const GRID = '#e6e8eb';
const AXIS_TEXT = '#767d87';

const isNum = (v: unknown) => v !== null && v !== undefined && v !== '' && typeof v !== 'boolean' && Number.isFinite(Number(v));

/** 1,284.5 with a true minus sign (−212), as the rest of LexiStar writes numbers. */
function group(n: number, digits: number): string {
  const s = Math.abs(n).toLocaleString('en-GB', { minimumFractionDigits: digits, maximumFractionDigits: digits });
  return n < 0 && Number(s.replace(/,/g, '')) !== 0 ? `−${s}` : s;
}

/** A number with its unit, as the rest of LexiStar writes them (R12,400 · 1,284 L · 92.4%). */
export function formatWithUnit(value: unknown, unit: string | null | undefined, decimals?: number | null): string {
  if (!isNum(value)) return value === null || value === undefined || value === '' ? '—' : String(value);
  const n = Number(value);
  const d = decimals ?? (Math.abs(n) < 10 && !Number.isInteger(n) ? 1 : 0);
  switch (unit) {
    case 'ZAR':
      return `${n < 0 ? '−' : ''}R${group(Math.abs(n), decimals ?? (Math.abs(n) < 100 && !Number.isInteger(n) ? 2 : 0))}`;
    case '%':
      return `${group(n, decimals ?? 1)}%`;
    case undefined:
    case null:
    case '':
    case 'count':
      return group(n, d);
    default:
      return `${group(n, d)} ${unit === 'L/100km' ? 'L/100 km' : unit}`;
  }
}

/** One table cell by column type (M4-R1 ColumnType). */
export function formatCell(value: unknown, col: ReportColumn): string {
  if (value === null || value === undefined || value === '') return '—';
  switch (col.kind) {
    case 'number':
    case 'integer':
      return formatWithUnit(value, col.unit, col.kind === 'integer' ? 0 : col.decimals);
    case 'percent':
      return formatWithUnit(value, '%', col.decimals);
    case 'duration_s':
      return fmtDuration(value);
    case 'datetime':
      return fmtDateTime(value);
    case 'boolean':
      return value === true || value === 'true' ? 'Yes' : 'No';
    default:
      return String(value);
  }
}

const NUMERIC_KINDS = ['number', 'integer', 'percent', 'duration_s'];

function Chart({ table, print }: { table: ProviderTable; print: boolean }) {
  const chart = table.chart;
  if (!chart || table.rows.length < 2) return null;
  const xCol = table.columns.find((c) => c.key === chart.x);
  const unit = chart.series[0]?.unit ?? null;
  const rows = table.rows.map((r) => {
    const out: Record<string, unknown> = { __x: r[chart.x] };
    chart.series.forEach((s) => {
      out[s.key] = isNum(r[s.key]) ? Number(r[s.key]) : null;
    });
    return out;
  });
  const fmt = (v: unknown) => formatWithUnit(v, unit);
  const tick = { fill: AXIS_TEXT, fontSize: 10.5 };
  const common = {
    data: rows,
    margin: { top: 8, right: 12, bottom: 4, left: 4 },
  };
  const axes = [
    <CartesianGrid key="g" vertical={false} stroke={GRID} strokeWidth={1} />,
    <XAxis
      key="x"
      dataKey="__x"
      tick={tick}
      tickLine={false}
      axisLine={{ stroke: GRID }}
      interval="preserveStartEnd"
      minTickGap={12}
      tickFormatter={(v: unknown) => (xCol?.kind === 'date' && typeof v === 'string' ? v.slice(5) : String(v ?? ''))}
    />,
    <YAxis
      key="y"
      tick={tick}
      tickLine={false}
      axisLine={false}
      width={64}
      tickFormatter={(v: unknown) => fmt(v)}
    />,
    <Tooltip
      key="t"
      isAnimationActive={false}
      formatter={(v: unknown, name: string) => [fmt(v), name]}
      contentStyle={{ fontSize: 12, borderColor: GRID }}
    />,
    ...(chart.series.length > 1 ? [<Legend key="l" iconType="plainline" wrapperStyle={{ fontSize: 11, color: AXIS_TEXT }} />] : []),
  ];
  return (
    <figure style={{ margin: '4px 0 10px' }} aria-label={`${table.title || 'Report'} chart`}>
      <div style={{ width: '100%', height: print ? 200 : 240 }}>
        <ResponsiveContainer width="100%" height="100%">
          {chart.type === 'line' ? (
            <LineChart {...common}>
              {axes}
              {chart.series.map((s, i) => (
                <Line
                  key={s.key}
                  type="linear"
                  dataKey={s.key}
                  name={s.label}
                  stroke={SERIES_COLORS[i] || SERIES_COLORS[0]}
                  strokeWidth={2}
                  dot={rows.length <= 31 ? { r: 3, strokeWidth: 2, stroke: '#ffffff', fill: SERIES_COLORS[i] || SERIES_COLORS[0] } : false}
                  isAnimationActive={false}
                  connectNulls
                />
              ))}
            </LineChart>
          ) : (
            <BarChart {...common} barGap={2} barCategoryGap="24%">
              {axes}
              {chart.series.map((s, i) => (
                <Bar
                  key={s.key}
                  dataKey={s.key}
                  name={s.label}
                  fill={SERIES_COLORS[i] || SERIES_COLORS[0]}
                  maxBarSize={24}
                  radius={[4, 4, 0, 0]}
                  isAnimationActive={false}
                />
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
      {chart.series.length === 1 && (
        <figcaption className="lxr-muted" style={{ fontSize: 10.5 }}>
          {chart.series[0]!.label} by {xCol?.label.toLowerCase() || 'row'} — the table below lists every value
        </figcaption>
      )}
    </figure>
  );
}

function Table({ table, print }: { table: ProviderTable; print: boolean }) {
  if (!table.rows.length) return <p className="lxr-empty">No rows for these parameters.</p>;
  return (
    <div className="lxr-scroll">
      <table className="lxr-table">
        <thead>
          <tr>
            {table.columns.map((c) => (
              <th key={c.key} className={NUMERIC_KINDS.includes(c.kind) ? 'lxr-num' : undefined}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((r, i) => (
            <tr key={String(r.id ?? i)}>
              {table.columns.map((c) => {
                const text = formatCell(r[c.key], c);
                const drill = typeof r.drill === 'string' ? r.drill : null;
                const first = c === table.columns[0];
                return (
                  <td key={c.key} className={NUMERIC_KINDS.includes(c.kind) ? 'lxr-num' : undefined}>
                    {c.kind === 'provenance' ? (
                      <>
                        <Prov provenance={r[c.key]} /> {text}
                      </>
                    ) : first && drill && !print ? (
                      <Link to={drill}>{text}</Link>
                    ) : (
                      text
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        {table.totals && (
          <tfoot>
            <tr>
              {table.columns.map((c, i) => (
                <td key={c.key} className={NUMERIC_KINDS.includes(c.kind) ? 'lxr-num' : undefined}>
                  {i === 0 && (table.totals![c.key] === undefined || table.totals![c.key] === null)
                    ? 'Total'
                    : table.totals![c.key] === undefined
                      ? ''
                      : formatCell(table.totals![c.key], c)}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

function Headline({ figures, print }: { figures: HeadlineFigure[]; print: boolean }) {
  if (!figures.length) return null;
  return (
    <dl className="lxr-kpis" aria-label="Headline figures">
      {figures.map((h) => (
        <div className="lxr-kpi" key={h.key}>
          <dt>{h.label}</dt>
          <dd>
            {!print && h.drill ? <Link to={h.drill}>{formatWithUnit(h.value, h.unit)}</Link> : formatWithUnit(h.value, h.unit)}
            <Prov provenance={h.provenance} />
            {h.state === 'provisional' && <small>provisional</small>}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export default function ReportBody({ report, print }: { report: ProviderReport; print: boolean }) {
  const [primary, ...rest] = report.tables;
  return (
    <>
      <Headline figures={report.headline} print={print} />
      <div className="lxr-stack">
        {primary ? (
          <Section title={primary.title || 'Detail'} aside={`${primary.rows.length} row${primary.rows.length === 1 ? '' : 's'}`}>
            <Chart table={primary} print={print} />
            <Table table={primary} print={print} />
          </Section>
        ) : (
          <p className="lxr-empty">The report returned no data for these parameters.</p>
        )}
        {rest.map((t) => (
          <Section key={t.key} title={t.title || 'Detail'} aside={`${t.rows.length} row${t.rows.length === 1 ? '' : 's'}`}>
            <Table table={t} print={print} />
          </Section>
        ))}
        {report.notes.length > 0 && (
          <Section title="Notes">
            {report.notes.map((n) => (
              <p key={n} className="lxr-prose">
                {n}
              </p>
            ))}
          </Section>
        )}
      </div>
    </>
  );
}
